/**
 * The menu on a phone (#237): under 880 px the top bar keeps Avisos and the role's main
 * destination, and a «Menú» button opens a sheet with the rest: the same `#nav` (the same groups,
 * the same disclosures, built from console/nav.ts) and the person's footer (theme, Salir). On the
 * desktop the sheet's wrapper is invisible to layout (`display: contents`) and nothing changes.
 *
 * While open the sheet is a modal dialog (`role="dialog"`, `aria-modal`, named by its title): the
 * rest of the page is inert and the page does not scroll, Tab and Shift+Tab go around inside it,
 * and Escape, the backdrop or «Cerrar menú» close it, with focus back on «Menú». Choosing a screen
 * closes it too, and the console moves focus to the screen.
 */

export type MenuSheetParts = {
  /** «Menú», in the top bar. */
  opener: HTMLButtonElement;
  /** The sheet: the menu and the person's footer. */
  sheet: HTMLElement;
  /** The backdrop beside the sheet: a tap on it closes the sheet. */
  scrim: HTMLElement;
  /** «Cerrar menú», at the top of the sheet. */
  closer: HTMLButtonElement;
  /** What stays out of reach while the sheet is open (the screen, the top bar's other controls). */
  outside: HTMLElement[];
  /** Whether the window is a phone's (880 px or less): the sheet exists only there. */
  phone: () => boolean;
};

export type MenuSheet = {
  open: () => void;
  /** Closes the sheet; focus goes back to «Menú» unless the console moves it elsewhere. */
  close: (returnFocus?: boolean) => void;
  isOpen: () => boolean;
};

/**
 * The controls Tab reaches inside an element, in order: no disabled one, none hidden (by the
 * attribute, theirs or an ancestor's) and none of a closed group of the menu (its options are
 * `display: none`, styles.css).
 */
export function focusables(root: ParentNode): HTMLElement[] {
  return [
    ...root.querySelectorAll<HTMLElement>('button, a[href], [tabindex]:not([tabindex="-1"])'),
  ].filter((el) => {
    if ((el as HTMLButtonElement).disabled || el.closest('[hidden], [inert]')) return false;
    const items = el.closest('.nav-items');
    const toggle = items?.previousElementSibling;
    return !(toggle?.matches('.nav-toggle') && toggle.getAttribute('aria-expanded') !== 'true');
  });
}

export function wireMenuSheet(parts: MenuSheetParts): MenuSheet {
  const { opener, sheet, scrim, closer, outside, phone } = parts;
  const isOpen = () => sheet.hasAttribute('data-open');

  const open = () => {
    if (isOpen() || !phone()) return;
    sheet.setAttribute('data-open', '');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-labelledby', 'menu-title');
    opener.setAttribute('aria-expanded', 'true');
    scrim.hidden = false;
    for (const el of outside) el.setAttribute('inert', '');
    document.documentElement.setAttribute('data-menu-open', '');
    // Where the person is: the option of the screen on view (its group is open), else the start.
    const here = sheet.querySelector<HTMLElement>('[aria-current="page"]');
    const reachable = focusables(sheet);
    (here && reachable.includes(here) ? here : reachable[0])?.focus();
  };

  const close = (returnFocus = true) => {
    if (!isOpen()) return;
    sheet.removeAttribute('data-open');
    sheet.removeAttribute('role');
    sheet.removeAttribute('aria-modal');
    sheet.removeAttribute('aria-labelledby');
    opener.setAttribute('aria-expanded', 'false');
    scrim.hidden = true;
    for (const el of outside) el.removeAttribute('inert');
    document.documentElement.removeAttribute('data-menu-open');
    if (returnFocus) opener.focus();
  };

  opener.addEventListener('click', () => (isOpen() ? close() : open()));
  closer.addEventListener('click', () => close());
  scrim.addEventListener('click', () => close());
  sheet.addEventListener('keydown', (ev) => {
    if (!isOpen()) return;
    if (ev.key === 'Escape') {
      ev.preventDefault();
      close();
      return;
    }
    if (ev.key !== 'Tab') return;
    // Around inside the sheet: past the last control to the first, before the first to the last.
    const reachable = focusables(sheet);
    const first = reachable[0];
    const last = reachable.at(-1);
    if (!first || !last) return;
    const at = document.activeElement;
    if (ev.shiftKey ? at === first || !sheet.contains(at) : at === last) {
      ev.preventDefault();
      (ev.shiftKey ? last : first).focus();
    }
  });
  // A window that grows past the phone's width has the sidebar again: no sheet left open.
  window.addEventListener('resize', () => {
    if (isOpen() && !phone()) close(false);
  });

  return { open, close, isOpen };
}
